import { Button, Col, Divider, Form, Input, Modal, Popconfirm, Row, Space, Spin, Timeline, Typography, message } from 'antd'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Case } from '.'
import { closeCase, getCases, retractCase, updateCase } from '../../api/arendeApi'
import { useApi } from '../../api/useApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import { DataType } from '../../components/tablesConfig/filterconfig'
import CaseModal from './components/caseModal/CaseModal'
import { KIND_LABEL, reasonLabel } from './components/handleFlag/reasons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
const { Text } = Typography
const { TextArea } = Input

export type INewLog = {
  id?: number
  createdBy?: number // change to user
  comment: string
}

const SingleCase = () => {
  const [textInput, setTextInput] = useState<string>('')
  const [currentCase, setCurrentCase] = useState<Case>()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>()
  const [retracting, setRetracting] = useState<boolean>(false)
  type Param = { id: string }
  const { id } = useParams<Param>()
  const getId = parseInt(id)
  const api = useApi()
  const navigate = useHistory()

  const { isLoading, data: cases } = useQuery(['cases'], getCases)
  const queryClient = useQueryClient()

  useEffect(() => {
    const findCase = cases?.find((item) => item.id === getId)
    setCurrentCase(findCase)
  }, [cases, getId])

  const editHandler = async () => {
    setLoading(true)

    const newLog: INewLog = {
      id: currentCase?.id,
      createdBy: currentCase?.ägare.id, // change to current user
      comment: textInput,
    }

    const response = await updateCase(api, newLog)
    if (response) {
      setLoading(false)
      queryClient.invalidateQueries(['cases'])

      response.created = new Date(response.created)
      setCurrentCase(response)
    }
  }

  const closeHandler = async () => {
    const newLog: INewLog = {
      id: currentCase?.id,
      createdBy: currentCase?.ägare.id, // change to current user
      comment: textInput,
    }

    const response = await closeCase(api, newLog)

    if (response) {
      setSuccess(true)
      response.created = new Date(response.created)
      response.senastUppdaterad = new Date(response.senastUppdaterad)
      setCurrentCase(response)
    }
  }

  /**
   * Ångrar hanteringen (#30 PR 9). Raden raderas inte — den återtas, försvinner ur
   * listan och slutar räknas in i flaggans läge.
   *
   * Navigerar tillbaka efteråt: hanteringen ligger inte längre i `['cases']`, så den
   * här vyn skulle annars bli tom utan att förklara varför.
   */
  const retractHandler = async () => {
    setRetracting(true)

    try {
      await retractCase(api, getId)
      await queryClient.invalidateQueries(['cases'])
      message.success('Hanteringen är ångrad')
      navigate.push('/app/arenden')
    } catch (e) {
      message.error('Kunde inte ångra hanteringen')
    } finally {
      setRetracting(false)
    }
  }

  /**
   * Dialogruta i stället för Popconfirm — samma bekräftelse som på flaggans detaljvy.
   * Bubblan blev klumpig så fort förklaringen fick plats, och dialogen är tydligare för
   * något som inte går att ångra tillbaka.
   */
  const bekräftaÅngra = () => {
    if (!currentCase) return

    Modal.confirm({
      title: 'Ångra hanteringen?',
      // Påstår INTE att flaggorna blir ohanterade: en flagga kan ha flera hanteringar,
      // och den här vyn känner bara till sin egen. Flaggans detaljvy vet mer och säger
      // där exakt vad som händer.
      content:
        currentCase.flags.length > 1
          ? `Hanteringen gäller ${currentCase.flags.length} flaggor och slutar gälla för alla. Hanteringen raderas inte, den slutar bara gälla.`
          : 'Hanteringen slutar gälla för flaggan. Hanteringen raderas inte, den slutar bara gälla.',
      okText: 'Ångra',
      cancelText: 'Avbryt',
      okButtonProps: { danger: true },
      onOk: retractHandler,
    })
  }

  const getColor = (status: string) => {
    switch (status) {
      case 'öppet':
        return 'red'
      case 'Uppdaterat':
        return 'grey'
      case 'Stängt':
        return 'red'
    }
  }

  const getStatus = (type: string) => {
    switch (type) {
      case 'created':
        return 'Uppföljningen skapades'
      case 'comment':
        return 'Anteckning'
      case 'closed':
    }
  }

  const deleteKey = (key: React.Key) => {
    console.log(key)
  }

  return (
    <>
      <ViewHeader heading={currentCase ? `Hantering ${currentCase.key}` : 'Hantering'} />
      <Row className="case-wrapper">
        <Col
          span={16}
          className="case-column"
          style={{
            padding: '20px 30px 0 0',
          }}
        >
          <div>
            <Text strong>Hanteringsnummer: </Text>
            <Text>{currentCase?.key}</Text>
          </div>
          <div>
            <Text strong>Handling: </Text>
            <Text>{currentCase ? KIND_LABEL[currentCase.kind] ?? currentCase.kind : ''}</Text>
          </div>
          {/* Skäl finns bara för avfärdad och hanterad; uppföljningar har beskrivningen i stället. */}
          {!!currentCase?.sub_reasons?.length && (
            <div>
              <Text strong>Skäl: </Text>
              <Text>{currentCase.sub_reasons.map(reasonLabel).join(' · ')}</Text>
            </div>
          )}
          <div>
            <Text strong>Skapad av: </Text>
            <Text>{currentCase?.ägare.namn}</Text>
          </div>
          {/* "Synlighet" borttaget — bekräftad död kod som aldrig användes i någon filtrering (#30). */}
          <div>
            <Text strong>Skapades: </Text>
            <Text>{currentCase?.created.toLocaleDateString()}</Text>
          </div>
          <div>
            <Text strong>Flaggor: </Text>
            {currentCase?.flags.map((item, index) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Text>
                  <Link
                    to={{
                      pathname: `/app/avvikelse/flag=${item.type}&id=${item.id}`,
                      state: { flagname: item.namn, riskniva: item.nivå },
                    }}
                  >
                    {item.namn}
                  </Link>
                </Text>
                <Text>Risknivå: {item.nivå}</Text>
                <Text>Skapad: {new Date(item.created).toLocaleDateString()}</Text>
                <Popconfirm title="Är du säker på att du vill radera detta ärende?" okText="Bekräfta" cancelText="Avbryt" onConfirm={() => deleteKey(currentCase.id)}>
                  <button className="table-edit-link">Radera</button>
                </Popconfirm>
              </div>
            ))}
          </div>
          {/* Avfärdad och hanterad skapas stängda och har inget att redigera — de får bara
              en rad om när det gjordes. Uppföljningar behåller anteckningsformuläret tills
              de stängs. Villkoret på `status` täcker båda fallen, men texten anpassas till
              handlingen så att ett avfärdande inte påstås ha "hanterats". */}
          {currentCase?.status === 'Stängt' ? (
            <>
              <div style={{ marginTop: 40 }}>
                <Text strong>
                  {currentCase.kind === 'avfärdad' ? 'Flaggan avfärdades' : currentCase.kind === 'hanterad' ? 'Flaggan markerades som hanterad' : 'Uppföljningen stängdes'} den{' '}
                  {moment(currentCase?.senastUppdaterad).format('YYYY-MM-DD, HH:mm')}
                </Text>
              </div>

              {/* Bara modalens två handlingar går att ångra. En uppföljning är stängd, inte
                  felklickad — där finns anteckningar och en historik som ångra-knappen inte
                  har något svar på. Backend avvisar den också. */}
              {(currentCase.kind === 'avfärdad' || currentCase.kind === 'hanterad') && (
                <div style={{ marginTop: 24 }}>
                  <Button danger loading={retracting} onClick={bekräftaÅngra}>
                    Ångra hantering
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <Divider />
              <Form onFinish={editHandler} autoComplete="off">
                <Text strong>Anteckning</Text>
                <Form.Item rules={[{ required: true, message: 'Anteckning krävs.' }]} name="note">
                  <TextArea rows={4} autoSize={{ minRows: 4, maxRows: 20 }} onChange={(e) => setTextInput(e.target.value)} />
                </Form.Item>
                <Space style={{ float: 'right', marginTop: 20 }}>
                  <Button htmlType="submit" loading={loading}>
                    Spara ändringar
                  </Button>
                  <Button type="primary" onClick={() => setShowModal(true)}>
                    Stäng uppföljning
                  </Button>
                </Space>
              </Form>
            </>
          )}
        </Col>

        <Col span={8} style={{ padding: 40 }} className="case-column">
          <Timeline>
            {currentCase?.händelser.map((item, index) => (
              <Timeline.Item key={index} color={getColor(item.status)}>
                <p>
                  {getStatus(item.type)} {new Date(item.date).toLocaleDateString()}
                </p>
                <p>{item.comment}</p>
              </Timeline.Item>
            ))}
          </Timeline>
        </Col>
      </Row>

      {isLoading && <Spin size="large" style={{ position: 'absolute', top: '50%', left: '50%' }} />}

      {showModal && (
        <CaseModal
          closeModal={success}
          sendData={closeHandler}
          cancelModal={() => setShowModal(false)}
          modalText={{
            text: 'Är du säker på att du vill stänga denna uppföljning?',
            loading: 'Hanterar uppföljningen..',
          }}
        />
      )}
    </>
  )
}

export default SingleCase
